<script lang="ts">
  import { onMount } from "svelte";
  import { mountChart, darkTooltip } from "../../lib/echartsHelpers";
  import type { DerivedWorkout } from "../../lib/lapAnalysis";
  import { fmtPaceTick } from "../../lib/format";
  import { strokeColor, strokeAbbrev, COLORS } from "../../lib/designTokens";
  import StrokePill from "../../lib/StrokePill.svelte";

  interface Props { dw: DerivedWorkout }
  let { dw }: Props = $props();

  let donutEl: HTMLDivElement;

  onMount(() => mountChart(donutEl, (chart) => chart.setOption({
    backgroundColor: "transparent",
    tooltip: {
      trigger: "item",
      ...darkTooltip((p: { name: string; value: number; percent: number }) =>
        `<strong>${p.name}</strong><br/>${p.value.toFixed(0)} m · ${p.percent.toFixed(1)}%`),
    },
    series: [
      {
        type: "pie",
        radius: ["55%", "85%"],
        avoidLabelOverlap: false,
        itemStyle: { borderColor: COLORS.navyMid, borderWidth: 2 },
        label: { show: false },
        labelLine: { show: false },
        data: dw.strokeBreakdown.map((s) => ({
          name: s.style,
          value: s.meters,
          itemStyle: { color: strokeColor(s.style) },
        })),
      },
    ],
  })));

  let dominantStroke = $derived(dw.strokeBreakdown[0]?.style ?? "—");
  let dominantPct = $derived(dw.strokeBreakdown[0]?.pctMeters ?? 0);
</script>

<article class="proto">
  <header>
    <span class="num">05</span>
    <h2>Stroke Mix + Lap Browse</h2>
    <p class="story">
      What kind of swim was this? Donut shows stroke distribution by meters. Tap a lap to browse details.
    </p>
  </header>

  <div class="top">
    <div class="donut" bind:this={donutEl}></div>
    <div class="legend">
      <div class="dom">
        <span class="muted">Mostly</span>
        <span class="dom-name" style:color={strokeColor(dominantStroke)}>{dominantStroke}</span>
        <span class="dom-pct">{dominantPct.toFixed(0)}%</span>
      </div>
      {#each dw.strokeBreakdown as s, i (i)}
        <div class="leg-row">
          <span class="dot" style:background={strokeColor(s.style)}></span>
          <span class="leg-name">{strokeAbbrev(s.style)}</span>
          <span class="leg-meters">{s.meters.toFixed(0)} m</span>
          <span class="leg-pct">{s.pctMeters.toFixed(0)}%</span>
        </div>
      {/each}
    </div>
  </div>

  <div class="lap-list">
    {#each dw.laps as lap (lap.index)}
      <div class="lap-card" class:peak={lap.index === dw.peakLapIndex}>
        <div class="left">
          <span class="lap-num">L{lap.index}</span>
          <StrokePill stroke={lap.strokeStyle} />
        </div>
        <div class="middle">
          <span class="time">{Math.floor(lap.durationSeconds)}<small>s</small></span>
          {#if lap.spl !== null}<span class="spl">{lap.spl} <small>SPL</small></span>{/if}
        </div>
        <div class="right">
          {#if lap.avgHr !== null}
            <span class="hr" style:color={COLORS.coralRed}>{lap.avgHr.toFixed(0)}<small> bpm</small></span>
          {/if}
          {#if lap.pacePer100s !== null}
            <span class="pace" style:color={COLORS.blueGreen}>{fmtPaceTick(lap.pacePer100s)}<small>/100</small></span>
          {/if}
        </div>
      </div>
    {/each}
  </div>
</article>

<style>
  .proto { background: var(--bg-card); border-radius: 12px; padding: 1rem; margin: 0.75rem 0; }
  header { margin-bottom: 1rem; }
  .num { display: inline-block; padding: 0.1rem 0.5rem; background: var(--accent-primary); color: var(--bg-canvas); font-size: 0.7rem; font-weight: 700; border-radius: 3px; letter-spacing: 1px; }
  h2 { font-size: 1.1rem; font-weight: 700; margin: 0.4rem 0 0.2rem; }
  .story { font-size: 0.78rem; color: var(--text-muted); margin: 0; }

  .top { display: grid; grid-template-columns: 140px 1fr; gap: 1rem; align-items: center; margin-bottom: 1rem; }
  .donut { width: 140px; height: 140px; }
  .legend { display: flex; flex-direction: column; gap: 0.3rem; font-size: 0.8rem; }
  .dom { margin-bottom: 0.4rem; line-height: 1.3; }
  .dom .muted { color: var(--text-muted); font-size: 0.7rem; display: block; text-transform: uppercase; letter-spacing: 1px; }
  .dom-name { font-weight: 700; font-size: 1.25rem; margin-right: 0.4rem; }
  .dom-pct { font-size: 0.85rem; color: var(--text-muted); font-variant-numeric: tabular-nums; }
  .leg-row { display: grid; grid-template-columns: 14px auto 1fr auto; gap: 0.4rem; align-items: center; font-variant-numeric: tabular-nums; }
  .dot { width: 10px; height: 10px; border-radius: 50%; }
  .leg-name { font-weight: 600; font-size: 0.75rem; letter-spacing: 0.5px; }
  .leg-meters { font-size: 0.75rem; color: var(--text-muted); }
  .leg-pct { font-size: 0.75rem; }

  .lap-list { display: flex; flex-direction: column; gap: 0.3rem; max-height: 360px; overflow-y: auto; padding-right: 4px; }
  .lap-card { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 0.75rem; padding: 0.5rem 0.6rem; background: var(--bg-canvas); border-radius: 6px; }
  .lap-card.peak { box-shadow: inset 3px 0 0 var(--accent-fire); }
  .left { display: flex; align-items: center; gap: 0.5rem; }
  .lap-num { color: var(--text-muted); font-size: 0.75rem; font-variant-numeric: tabular-nums; min-width: 28px; }
  .middle { display: flex; gap: 0.75rem; font-variant-numeric: tabular-nums; font-size: 0.85rem; }
  .middle small { color: var(--text-muted); font-size: 0.65rem; margin-left: 0.1rem; }
  .right { display: flex; gap: 0.5rem; font-variant-numeric: tabular-nums; font-size: 0.85rem; font-weight: 600; }
  .right small { font-size: 0.65rem; font-weight: 400; margin-left: 0.1rem; }
</style>
